import { alphaBlend } from '@napolab/alpha-blend';
import { extractPaletteColor } from 'base/styleguide/color';
import {
  assertColorVariable,
  computeForegroundColor,
} from 'base/styleguide/concept';
import {
  ColorVariable,
  CompiledScheme,
  SchemeVariable,
} from 'base/styleguide/types';
import {
  ConceptAPI,
  getGlobalConceptsStore,
} from 'blocks/editor/styleguide/concept_store';
import styledPlaceholder1Image from '../styled-placeholder-1.file.svg';
import styledPlaceholder2Image from '../styled-placeholder-2.file.svg';

export const computeImagePlaceholderColor = (
  bgColor: ColorVariable,
  dereference: (color: ColorVariable) => string,
) => {
  const bgShadeStr = bgColor.match(/-(\d+)$/)?.[1];

  if (!bgShadeStr) {
    // default 'schemeless' style
    return {
      placeholderForeground: 'black',
      placeholderBackground: 'transparent',
      placeholderImage: `url(${styledPlaceholder1Image})`,
    };
  }

  const bgShade = parseInt(bgShadeStr, 10);
  const chromatic = bgColor.split('-')[0];

  let placeholderBg: ColorVariable;
  let placeholderFg: ColorVariable;
  let placeholderImage: string;
  let opacity: number;

  if (chromatic === 'neutral') {
    switch (bgShade) {
      case 0 /* white */:
        placeholderBg = 'neutral-shade-6';
        placeholderFg = 'neutral-shade-6';
        placeholderImage = styledPlaceholder1Image;
        opacity = 0.12;
        break;
      case 1:
      case 2:
        placeholderBg = 'neutral-shade-6';
        placeholderFg = 'neutral-shade-6';
        placeholderImage = styledPlaceholder1Image;
        opacity = 0.2;
        break;
      case 3:
        placeholderBg = 'neutral-shade-1';
        placeholderFg = 'neutral-white';
        placeholderImage = styledPlaceholder2Image;
        opacity = 0.2;
        break;
      case 4:
        placeholderBg = 'neutral-shade-1';
        placeholderFg = 'neutral-white';
        placeholderImage = styledPlaceholder2Image;
        opacity = 0.4;
        break;
      case 5:
        placeholderBg = 'neutral-shade-6';
        placeholderFg = 'neutral-white';
        placeholderImage = styledPlaceholder2Image;
        opacity = 0.3;
        break;
      case 6:
        placeholderBg = 'neutral-shade-5';
        placeholderFg = 'neutral-white';
        placeholderImage = styledPlaceholder2Image;
        opacity = 0.8;
        break;
      case 7 /* darkest */:
        placeholderBg = 'neutral-shade-5';
        placeholderFg = 'neutral-white';
        placeholderImage = styledPlaceholder2Image;
        opacity = 0.4;
        break;
      default:
        throw new Error(`Invalid shade: ${bgShade}`);
    }
  } else {
    switch (bgShade) {
      case 1 /* lightest */:
        placeholderBg = assertColorVariable(`${chromatic}-shade-4`);
        placeholderFg = assertColorVariable(`${chromatic}-shade-4`);
        placeholderImage = styledPlaceholder1Image;
        opacity = 0.1;
        break;
      case 2:
        placeholderBg = assertColorVariable(`${chromatic}-shade-4`);
        placeholderFg = assertColorVariable(`${chromatic}-shade-4`);
        placeholderImage = styledPlaceholder1Image;
        opacity = 0.3;
        break;
      case 3:
      case 4:
        placeholderBg = assertColorVariable(`${chromatic}-shade-7`);
        placeholderFg = 'neutral-white';
        placeholderImage = styledPlaceholder2Image;
        opacity = 0.15;
        break;
      case 5:
        placeholderBg = assertColorVariable(`${chromatic}-shade-7`);
        placeholderFg = 'neutral-white';
        placeholderImage = styledPlaceholder2Image;
        opacity = 0.55;
        break;
      case 6:
        placeholderBg = assertColorVariable(`${chromatic}-shade-4`);
        placeholderFg = 'neutral-white';
        placeholderImage = styledPlaceholder2Image;
        opacity = 0.45;
        break;
      case 7 /* darkest */:
        placeholderBg = assertColorVariable(`${chromatic}-shade-4`);
        placeholderFg = 'neutral-white';
        placeholderImage = styledPlaceholder2Image;
        opacity = 0.3;
        break;
      default:
        throw new Error(`Invalid shade: ${bgShade}`);
    }
  }

  const placeholderBgHex = dereference(placeholderBg);
  // convert opacity to hex (00-FF)
  const opacityHex = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0');

  const placeholderFgHex = dereference(placeholderFg);

  const fgHex = dereference(computeForegroundColor(bgColor));

  return {
    placeholderForeground: placeholderFgHex,
    // Apply the transparent placeholder background over the scheme's foreground color
    placeholderBackground: alphaBlend(
      `${fgHex}FF`,
      `${placeholderBgHex}${opacityHex}`,
    ),
    placeholderImage: `url(${placeholderImage})`,
  };
};

export function compiledSchemeVariables(
  scheme: CompiledScheme,
): Record<SchemeVariable, ColorVariable> {
  return {
    'color-scheme-1-background': scheme.background,
    'color-scheme-1-foreground': scheme.foreground,
    'color-scheme-1-text': scheme.text,
    'color-scheme-1-accent': scheme.accent,
    'color-scheme-1-border': scheme.border,
  };
}

export function hackedSchemeVariables(
  scheme: CompiledScheme,
  concept: ConceptAPI,
): Record<string, string> {
  const conceptsStore = getGlobalConceptsStore();

  const preferDarkLogo = scheme.alternate.has('logo');
  const lightLogoExists =
    conceptsStore.getRenderedLogo('light')?.value?.src != null;
  const darkLogoExists =
    conceptsStore.getRenderedLogo('dark')?.value?.src != null;
  const showDarkLogo =
    (preferDarkLogo && darkLogoExists) ||
    (!preferDarkLogo && darkLogoExists && !lightLogoExists);
  const showLightLogo =
    (!preferDarkLogo && lightLogoExists) ||
    (preferDarkLogo && lightLogoExists && !darkLogoExists);
  const showLogoPlaceholder = !lightLogoExists && !darkLogoExists;

  const { placeholderForeground, placeholderBackground, placeholderImage } =
    computeImagePlaceholderColor(scheme.background, (color) =>
      extractPaletteColor(concept, color),
    );

  return {
    '--logo-display-dark': showDarkLogo ? 'block' : 'none',
    '--logo-display-light': showLightLogo ? 'block' : 'none',
    '--logo-display-placeholder': showLogoPlaceholder ? 'block' : 'none',

    '--placeholder-foreground': placeholderForeground,
    '--placeholder-background': placeholderBackground,
    '--placeholder-image': placeholderImage,
  };
}
